<template>
	<AppIcon
		:name="mappedIconName"
		:size="mappedSize"
		:class="[colorClasses, className]"
		:aria-label="ariaLabel"
	/>
</template>

<script setup lang="ts">
/**
 * SvgIcon - Wrapper for backward compatibility with marketing components
 *
 * Maps old IconLibrary names to Lucide icons via AppIcon.
 * All icons are bundled at compile time via unplugin-icons.
 */
import { computed } from 'vue';
import AppIcon from '@/components/icons/AppIcon.vue';
import type { IconName } from './IconLibrary';

interface Props {
	name: IconName;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
	color?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'gray' | 'white' | 'current';
	className?: string;
	ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
	size: 'md',
	color: 'current',
});

// Map old IconLibrary names to Lucide icon names
const iconNameMap: Record<IconName, string> = {
	brain: 'lucide:brain',
	checkCircle: 'lucide:check-circle',
	lightning: 'lucide:zap',
	target: 'lucide:target',
	shield: 'lucide:shield',
	users: 'lucide:users',
	star: 'lucide:star',
	barChart: 'lucide:bar-chart-2',
	clock: 'lucide:clock',
	lightbulb: 'lucide:lightbulb',
	arrowRight: 'lucide:arrow-right',
	download: 'lucide:download',
	mail: 'lucide:mail',
	lock: 'lucide:lock',
	trendingUp: 'lucide:trending-up',
	gift: 'lucide:gift',
	helpCircle: 'lucide:help-circle',
	zap: 'lucide:zap',
	award: 'lucide:award',
	smile: 'lucide:smile',
	book: 'lucide:book-open',
	compass: 'lucide:compass',
};

const mappedIconName = computed(() => iconNameMap[props.name] || 'lucide:help-circle');

// Map size to AppIcon size (SvgIcon md = 6, AppIcon md = 5)
const sizeMap: Record<string, 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'> = {
	xs: 'xs',
	sm: 'sm',
	md: 'lg', // SvgIcon md (w-6) maps to AppIcon lg (w-6)
	lg: 'xl', // SvgIcon lg (w-8) maps to AppIcon xl (w-8)
	xl: '2xl', // SvgIcon xl (w-10) maps to AppIcon 2xl (w-10)
	'2xl': '2xl',
};

const mappedSize = computed(() => sizeMap[props.size] || 'lg');

const colorClasses = computed(() => {
	const colors: Record<string, string> = {
		primary: 'text-blue-600',
		success: 'text-green-600',
		warning: 'text-yellow-600',
		danger: 'text-red-600',
		info: 'text-cyan-600',
		gray: 'text-gray-600',
		white: 'text-white',
		current: 'text-current',
	};
	return colors[props.color] || colors.current;
});
</script>
